import { DOCUMENT } from '@angular/common';
import { APP_INITIALIZER, type FactoryProvider, inject, LOCALE_ID } from '@angular/core';

import { catchError, firstValueFrom, map, of, startWith } from 'rxjs';

import { TranslocoService } from '@jsverse/transloco';
import {
  TUI_ARABIC_LANGUAGE,
  TUI_ENGLISH_LANGUAGE,
  TUI_FRENCH_LANGUAGE,
  TUI_LANGUAGE,
  type TuiLanguage,
} from '@taiga-ui/i18n';

import { loadAllTranslocoScopes } from '@finverity/i18n';

import { FinLocalesService } from '@finverity/platform-app/core/services';

export const ACTIVE_LOCALE_PROVIDER: FactoryProvider = {
  provide: LOCALE_ID,
  useFactory: (): string => inject(FinLocalesService).getActiveLang(),
};

export const TUI_LANGUAGE_PROVIDER: FactoryProvider = {
  provide: TUI_LANGUAGE,
  // TUI_LANGUAGE expects an Observable<TuiLanguage>. Drive it off Transloco's langChanges$ so every
  // Taiga component that reads it (tui-input-date, tui-input-date-range, calendars, pagination, etc.)
  // re-renders in the new language the moment the locale switches — instead of being frozen at the
  // language captured during app bootstrap.
  useFactory: () => {
    const transloco = inject(TranslocoService);

    return transloco.langChanges$.pipe(
      startWith(transloco.getActiveLang()),
      map((lang): TuiLanguage =>
        lang === 'fr-FR' ? TUI_FRENCH_LANGUAGE : lang === 'ar' ? TUI_ARABIC_LANGUAGE : TUI_ENGLISH_LANGUAGE
      )
    );
  },
};

export const LOCALE_APP_INITIALIZER: FactoryProvider = {
  provide: APP_INITIALIZER,
  multi: true,
  useFactory: () => {
    const localesService = inject(FinLocalesService);
    const transloco = inject(TranslocoService);
    const document = inject(DOCUMENT);

    return async (): Promise<void> => {
      await localesService.initUserLocale();
      const lang = localesService.getActiveLang();
      transloco.setActiveLang(lang);
      // Two distinct failure modes, two guards: transloco returns EMPTY when the failed lang is its
      // own fallbackLang, and throws TranslationLoadError otherwise. Either one would reject this
      // initializer and leave the app unmounted on the index.html splash. `prodMode` silences
      // transloco's own logging, so surface the cause here instead of a bare RxJS error.
      await firstValueFrom(
        transloco.load(lang).pipe(
          catchError((error: unknown) => {
            console.error(`i18n: failed to load "${lang}" from /assets/i18n/${lang}/common.json`, error);

            return of(undefined);
          })
        ),
        { defaultValue: undefined }
      );
      // Preload every lazy scope for the RESOLVED language here (not via a separate
      // APP_INITIALIZER) so synchronous translate() in data-grid column-defs etc. resolves.
      // Doing it before setActiveLang settles — e.g. provideTranslocoPreloadAllScopes() reading
      // the bootstrap-default lang — preloads the wrong language and leaves raw header keys in fr-FR.
      await loadAllTranslocoScopes(transloco, lang);
      const dirFor = (value: string): 'rtl' | 'ltr' => (value.startsWith('ar') ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', lang);
      document.documentElement.setAttribute('dir', dirFor(lang));

      // Re-preload every lazy scope on each RUNTIME language switch. Without this, a language change
      // after bootstrap leaves the new language's scopes lazily loaded, so synchronous translate()
      // (instrument-details transformers, data-grid column-defs, etc.) returns raw keys until the
      // scope happens to load on demand.
      transloco.langChanges$.subscribe(newLang => {
        document.documentElement.setAttribute('lang', newLang);
        document.documentElement.setAttribute('dir', dirFor(newLang));
        void loadAllTranslocoScopes(transloco, newLang);
      });
    };
  },
};
